import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, CheckCircle2, Loader2, ShieldCheck, X } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { ModuleLayout } from '../../../components/layout/ModuleLayout';
import { canGovernBlueprints } from '../services/governancePermissions';
import { useExcelenciaFiltersStore } from '../state/excelenciaFilters.store';
import { useExcelenciaStore } from '../store';
import { PermissionGate } from '../components/shared/PermissionGate';
import { BlueprintGrid } from '../components/grid/BlueprintGrid';
import { GovernancaRibbon } from '../components/GovernancaRibbon';
import { BlueprintTechnicalDrawer } from '../components/drawer/BlueprintTechnicalDrawer';
import { VersionPanel } from '../components/VersionPanel';
import { BlueprintLogPanel } from '../components/BlueprintLogPanel';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { CreateBlueprintModal } from '../modals/CreateBlueprintModal';
import { EditBlueprintModal } from '../modals/EditBlueprintModal';
import type { CreateBlueprintResult } from '../modals/CreateBlueprintModal';
import type { BlueprintListItem } from '../services/blueprint.service';
import type { BlueprintCategory, BlueprintStage, ProcessBlueprint } from '../../../types/processBlueprint';
import { CATEGORY_LABELS } from '../../../types/processBlueprint';

function getConfirmMeta(state: { action: string; blueprint: ProcessBlueprint } | null) {
  if (!state) return { title: '', description: '', confirmLabel: 'Confirmar', danger: false };
  const name = state.blueprint.name;
  switch (state.action) {
    case 'publish':
      return {
        title: 'Publicar Blueprint',
        description: `Deseja publicar "${name}"? Ele ficara disponivel para uso em projetos.`,
        confirmLabel: 'Publicar',
        danger: false,
      };
    case 'archive':
      return {
        title: 'Arquivar Blueprint',
        description: `Deseja arquivar "${name}"? Esta acao pode ser revertida posteriormente.`,
        confirmLabel: 'Arquivar',
        danger: false,
      };
    case 'delete':
      return {
        title: 'Excluir Blueprint',
        description: `Tem certeza que deseja excluir "${name}"? Esta acao e irreversivel.`,
        confirmLabel: 'Excluir',
        danger: true,
      };
    case 'setStandard':
      return {
        title: 'Definir como Padrao',
        description: `Deseja definir "${name}" como padrao corporativo?`,
        confirmLabel: 'Definir Padrao',
        danger: false,
      };
    case 'removeStandard':
      return {
        title: 'Remover Padrao',
        description: `Deseja remover o status de padrao corporativo de "${name}"?`,
        confirmLabel: 'Remover Padrao',
        danger: false,
      };
    default:
      return { title: '', description: '', confirmLabel: 'Confirmar', danger: false };
  }
}

export const ExcelenciaDashboard: React.FC = () => {
  const { user } = useAuth();
  const organizationId = user?.organizationId || '';
  const canGovernance = canGovernBlueprints(user?.role);
  const [selectedBlueprintId, setSelectedBlueprintId] = useState<string | null>(null);
  const [selectedGridItem, setSelectedGridItem] = useState<BlueprintListItem | null>(null);
  const [gridRefreshToken, setGridRefreshToken] = useState(0);

  const {
    status,
    category,
    search,
    setStatus,
    setCategory,
    setSearch,
  } = useExcelenciaFiltersStore();

  const setOrganizationId = useExcelenciaStore((state) => state.setOrganizationId);
  const reload = useExcelenciaStore((state) => state.reload);
  const error = useExcelenciaStore((state) => state.error);
  const blueprints = useExcelenciaStore((state) => state.blueprints);
  const processingId = useExcelenciaStore((state) => state.processingId);
  const toast = useExcelenciaStore((state) => state.toast);
  const clearToast = useExcelenciaStore((state) => state.clearToast);
  const showCreateModal = useExcelenciaStore((state) => state.showCreateModal);
  const closeCreateModal = useExcelenciaStore((state) => state.closeCreateModal);
  const editTarget = useExcelenciaStore((state) => state.editTarget);
  const closeEditModal = useExcelenciaStore((state) => state.closeEditModal);
  const versionTarget = useExcelenciaStore((state) => state.versionTarget);
  const closeVersionPanel = useExcelenciaStore((state) => state.closeVersionPanel);
  const logTarget = useExcelenciaStore((state) => state.logTarget);
  const closeLogPanel = useExcelenciaStore((state) => state.closeLogPanel);
  const usageTarget = useExcelenciaStore((state) => state.usageTarget);
  const usageData = useExcelenciaStore((state) => state.usageData);
  const usageLoading = useExcelenciaStore((state) => state.usageLoading);
  const closeUsagePanel = useExcelenciaStore((state) => state.closeUsagePanel);
  const confirmState = useExcelenciaStore((state) => state.confirmState);
  const confirmLoading = useExcelenciaStore((state) => state.confirmLoading);
  const executeConfirm = useExcelenciaStore((state) => state.executeConfirm);
  const cancelConfirm = useExcelenciaStore((state) => state.cancelConfirm);
  const storeCreateManual = useExcelenciaStore((state) => state.createManual);
  const storeCreateFromMap = useExcelenciaStore((state) => state.createFromMap);
  const storeCreateFromWorkflow = useExcelenciaStore((state) => state.createFromWorkflow);
  const storeClone = useExcelenciaStore((state) => state.cloneBlueprint);
  const storeCreateNewVersion = useExcelenciaStore((state) => state.createNewVersion);
  const storeEditBlueprint = useExcelenciaStore((state) => state.editBlueprint);
  const loadAvailableMaps = useExcelenciaStore((state) => state.loadAvailableMaps);
  const loadAvailableWorkflows = useExcelenciaStore((state) => state.loadAvailableWorkflows);
  const loadVersions = useExcelenciaStore((state) => state.loadVersions);
  const openCreateModal = useExcelenciaStore((state) => state.openCreateModal);
  const openEditModal = useExcelenciaStore((state) => state.openEditModal);
  const openVersionPanel = useExcelenciaStore((state) => state.openVersionPanel);
  const openLogPanel = useExcelenciaStore((state) => state.openLogPanel);
  const openUsagePanel = useExcelenciaStore((state) => state.openUsagePanel);
  const requestConfirm = useExcelenciaStore((state) => state.requestConfirm);
  const setToast = useExcelenciaStore((state) => state.setToast);

  const selectedBlueprint = useMemo(
    () => blueprints.find((blueprint) => blueprint.id === selectedBlueprintId) ?? null,
    [blueprints, selectedBlueprintId],
  );

  const availableCategories = useMemo(() => {
    const set = new Set<string>();
    for (const bp of blueprints) {
      if (bp.category) set.add(bp.category);
    }
    // Garantir que a categoria atualmente selecionada permaneça visível
    if (category && category !== 'all') set.add(category);
    return Array.from(set)
      .map((value) => ({ value, label: CATEGORY_LABELS[value] ?? value }))
      .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
  }, [blueprints, category]);

  const bumpGridRefresh = useCallback(() => {
    setGridRefreshToken((current) => current + 1);
  }, []);

  // Memoize callbacks passed to BlueprintGrid to avoid re-render loops
  const handleGridItemsChange = useCallback(
    (items: BlueprintListItem[]) => {
      if (!selectedBlueprintId) return;
      const current = items.find((item) => item.id === selectedBlueprintId) ?? null;
      if (current) setSelectedGridItem(current);
    },
    [selectedBlueprintId],
  );

  const handleGridSelect = useCallback(
    (item: BlueprintListItem) => {
      setSelectedBlueprintId(item.id);
      setSelectedGridItem(item);
    },
    [],
  );

  const handleGridEdit = useCallback(
    (item: BlueprintListItem) => openEditModal(item),
    [openEditModal],
  );

  const handleGridHistory = useCallback(
    (item: BlueprintListItem) => openVersionPanel(item),
    [openVersionPanel],
  );

  const handleGridAudit = useCallback(
    (item: BlueprintListItem) => openLogPanel(item),
    [openLogPanel],
  );

  useEffect(() => {
    if (!organizationId) return;
    setOrganizationId(organizationId);
    // Deferred reload â€â€ prevents blocking first paint of BlueprintGrid.
    // BlueprintGrid uses React Query for its own fast render path.
    const id = requestAnimationFrame(() => void reload());
    return () => cancelAnimationFrame(id);
  }, [organizationId, reload, setOrganizationId]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(clearToast, 4000);
    return () => clearTimeout(timer);
  }, [toast, clearToast]);

  useEffect(() => {
    if (!selectedBlueprintId) return;
    const exists = blueprints.some((blueprint) => blueprint.id === selectedBlueprintId);
    if (!exists) {
      setSelectedBlueprintId(null);
      setSelectedGridItem(null);
    }
  }, [blueprints, selectedBlueprintId]);

  const confirmMeta = useMemo(() => getConfirmMeta(confirmState), [confirmState]);

  const handlePublish = useCallback(
    (blueprint: ProcessBlueprint) => requestConfirm('publish', blueprint),
    [requestConfirm],
  );

  const handleArchive = useCallback(
    (blueprint: ProcessBlueprint) => requestConfirm('archive', blueprint),
    [requestConfirm],
  );

  const handleCreateVersion = useCallback(
    (blueprint: ProcessBlueprint) => {
      void storeCreateNewVersion(blueprint.id, undefined, user?.id).then(() => {
        bumpGridRefresh();
      });
    },
    [bumpGridRefresh, storeCreateNewVersion, user],
  );

  const handleConfirmExecute = useCallback(() => {
    void executeConfirm(user?.id).then(() => {
      bumpGridRefresh();
    });
  }, [bumpGridRefresh, executeConfirm, user]);

  const handleCreate = useCallback(
    async (params: CreateBlueprintResult) => {
      try {
        let created: ProcessBlueprint | undefined;

        if (params.origin === 'scratch') {
          created = await storeCreateManual({
            name: params.name,
            description: params.description,
            category: (params.category ?? 'geral') as BlueprintCategory,
            stages: params.stages,
            createdBy: user?.id,
          });
        } else if (params.origin === 'map' && params.mapId) {
          created = await storeCreateFromMap({
            name: params.name,
            description: params.description,
            category: (params.category ?? 'geral') as BlueprintCategory,
            mapId: params.mapId,
            createdBy: user?.id,
          });
        } else if (params.origin === 'workflow' && params.templateId) {
          created = await storeCreateFromWorkflow({
            name: params.name,
            description: params.description,
            category: (params.category ?? 'geral') as BlueprintCategory,
            templateId: params.templateId,
            createdBy: user?.id,
          });
        }

        if (created) {
          setToast({ type: 'success', message: `Blueprint "${params.name}" criado com sucesso` });
          closeCreateModal();
          openEditModal(created);
          setSelectedBlueprintId(created.id);
          bumpGridRefresh();
        }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Erro ao criar blueprint';
        setToast({ type: 'error', message });
      }
    },
    [
      bumpGridRefresh,
      closeCreateModal,
      openEditModal,
      setToast,
      storeCreateFromMap,
      storeCreateFromWorkflow,
      storeCreateManual,
      user,
    ],
  );

  const handleEditSave = useCallback(
    async (
      blueprintId: string,
      changes: {
        name?: string;
        description?: string;
        category?: BlueprintCategory;
        stages?: BlueprintStage[];
      },
      changeSummary?: string,
    ) => {
      await storeEditBlueprint(blueprintId, changes, changeSummary, user?.id);
      bumpGridRefresh();
    },
    [bumpGridRefresh, storeEditBlueprint, user],
  );

  const handleLoadMaps = useCallback(async () => {
    if (!user?.id) return [];
    return loadAvailableMaps(user.id);
  }, [loadAvailableMaps, user]);

  const handleLoadWorkflows = useCallback(async () => {
    return loadAvailableWorkflows();
  }, [loadAvailableWorkflows]);

  const handleDuplicate = useCallback(async () => {
    if (!selectedBlueprint) return;
    await storeClone(selectedBlueprint.id, `${selectedBlueprint.name} copia`);
    bumpGridRefresh();
  }, [bumpGridRefresh, selectedBlueprint, storeClone]);

  const handleRefresh = useCallback(async () => {
    await reload();
    bumpGridRefresh();
  }, [bumpGridRefresh, reload]);

  const ribbon = (
    <GovernancaRibbon
      canManage={canGovernance}
      hasSelection={Boolean(selectedBlueprint)}
      status={status}
      category={category}
      availableCategories={availableCategories}
      searchValue={search}
      onCreate={openCreateModal}
      onDuplicate={handleDuplicate}
      onOpenHistory={() => selectedBlueprint && openVersionPanel(selectedBlueprint)}
      onOpenAudit={() => selectedBlueprint && openLogPanel(selectedBlueprint)}
      onRefresh={handleRefresh}
      onStatusChange={setStatus}
      onCategoryChange={setCategory}
      onSearchChange={setSearch}
    />
  );

  const fallback = (
    <div className="flex h-full items-center justify-center px-6 text-sm text-slate-500">
      Acesso restrito ao controle estrutural de blueprints.
    </div>
  );

  return (
    <PermissionGate organizationId={organizationId} ability="BLUEPRINT_VIEW" fallback={fallback}>
      <ModuleLayout ribbon={ribbon}>
        <div className="flex h-full min-h-0 flex-col overflow-hidden dark:bg-black">

          <div className="min-h-0 flex-1">
            {error ? (
              <div className="flex h-full items-center justify-center px-6">
                <div className="max-w-md text-center">
                  <AlertCircle size={36} className="mx-auto mb-3 text-red-500" />
                  <h3 className="text-sm font-semibold text-red-600 dark:text-red-400">Falha ao carregar governanca</h3>
                  <p className="mt-1 text-xs text-red-500/80 dark:text-red-400/80">{error.message}</p>
                  <button
                    type="button"
                    onClick={() => void handleRefresh()}
                    className="mt-4 rounded-[4px] border border-red-200 px-4 py-2 text-xs font-semibold text-red-700 hover:bg-red-50 dark:border-red-900/60 dark:text-red-300 dark:hover:bg-red-950/30"
                  >
                    Recarregar
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex h-full min-h-0">
                <div className="min-h-0 flex-1">
                  <BlueprintGrid
                    organizationId={organizationId}
                    selectedId={selectedBlueprintId}
                    refreshToken={gridRefreshToken}
                    showFilters={false}
                    onItemsChange={handleGridItemsChange}
                    onSelect={handleGridSelect}
                    onEdit={handleGridEdit}
                    onOpenHistory={handleGridHistory}
                    onOpenAudit={handleGridAudit}
                  />
                </div>

                <BlueprintTechnicalDrawer
                  open={Boolean(selectedBlueprintId)}
                  blueprintId={selectedBlueprintId}
                  organizationId={organizationId}
                  summary={selectedGridItem}
                  onClose={() => {
                    setSelectedBlueprintId(null);
                    setSelectedGridItem(null);
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </ModuleLayout>

      <VersionPanel
        selectedBlueprint={versionTarget}
        allBlueprints={blueprints}
        onCreateVersion={handleCreateVersion}
        onClose={closeVersionPanel}
        isProcessing={processingId !== null}
        loadVersions={loadVersions}
      />

      <CreateBlueprintModal
        open={showCreateModal}
        onClose={closeCreateModal}
        onCreate={handleCreate}
        loadMaps={handleLoadMaps}
        loadWorkflows={handleLoadWorkflows}
      />

      <EditBlueprintModal blueprint={editTarget} onClose={closeEditModal} onSave={handleEditSave} />

      <BlueprintLogPanel blueprint={logTarget} onClose={closeLogPanel} />

      {usageTarget ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="w-full max-w-md overflow-hidden rounded-[6px] border border-slate-300 bg-white shadow-2xl dark:border-slate-800 dark:bg-[var(--bg-surface)]">
            <div className="flex items-center justify-between border-b border-slate-300 px-5 py-4 dark:border-slate-800">
              <div className="flex items-center gap-2">
                <ShieldCheck size={18} className="text-slate-500 dark:text-slate-300" />
                <h2 className="text-sm font-bold text-slate-900 dark:text-slate-100">Uso em Projetos</h2>
              </div>
              <button
                type="button"
                onClick={closeUsagePanel}
                className="rounded-[4px] p-1.5 transition-colors hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                <X size={16} className="text-slate-500" />
              </button>
            </div>

            <div className="border-b border-slate-200 bg-slate-50 px-5 py-3 dark:border-slate-800 dark:bg-slate-950">
              <p className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">{usageTarget.name}</p>
              <p className="text-[11px] text-slate-500 dark:text-slate-400">
                v{usageTarget.versionSemver} ÂÂ· {usageTarget.stages.length} etapas
              </p>
            </div>

            <div className="max-h-[320px] overflow-y-auto px-5 py-4">
              {usageLoading ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 size={16} className="animate-spin text-slate-500" />
                  <span className="ml-2 text-xs text-slate-500">Carregando...</span>
                </div>
              ) : null}

              {!usageLoading && usageData.length === 0 ? (
                <div className="py-6 text-center">
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Este blueprint nao esta vinculado a nenhum projeto.
                  </p>
                </div>
              ) : null}

              {!usageLoading && usageData.length > 0 ? (
                <div className="space-y-2">
                  <p className="mb-2 text-[11px] font-medium text-slate-700 dark:text-slate-300">
                    Vinculado em {usageData.length} projeto{usageData.length !== 1 ? 's' : ''}
                  </p>
                  {usageData.map((usage) => (
                    <div
                      key={usage.id}
                      className="flex items-center justify-between rounded-[4px] border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-900"
                    >
                      <div>
                        <span className="text-xs font-mono text-slate-700 dark:text-slate-300">
                          {usage.projectId.slice(0, 8)}...
                        </span>
                        {usage.notes ? (
                          <span className="ml-2 text-[10px] text-slate-500 dark:text-slate-400">{usage.notes}</span>
                        ) : null}
                      </div>
                      <span className="text-[10px] text-slate-400 dark:text-slate-500">
                        {new Date(usage.linkedAt).toLocaleDateString('pt-BR')}
                      </span>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>

            <div className="flex justify-end border-t border-slate-200 px-5 py-3 dark:border-slate-800">
              <button
                type="button"
                onClick={closeUsagePanel}
                className="rounded-[4px] px-3 py-1.5 text-xs text-slate-600 transition-colors hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {toast ? (
        <div className="fixed bottom-6 right-6 z-50 animate-in slide-in-from-bottom-4 fade-in duration-300">
          <div
            className={`flex min-w-[280px] max-w-[420px] items-center gap-3 rounded border pl-4 pr-3 py-3 shadow-lg backdrop-blur-sm ${
              toast.type === 'success'
                ? 'border-emerald-200 bg-emerald-50/95 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/90 dark:text-emerald-300'
                : 'border-red-200 bg-red-50/95 text-red-700 dark:border-red-800 dark:bg-red-950/90 dark:text-red-300'
            }`}
          >
            {toast.type === 'success' ? (
              <CheckCircle2 size={16} className="shrink-0" />
            ) : (
              <AlertCircle size={16} className="shrink-0" />
            )}
            <span className="flex-1 text-sm font-medium">{toast.message}</span>
            <button
              type="button"
              onClick={clearToast}
              className="shrink-0 rounded p-1 hover:bg-black/5 dark:hover:bg-white/5"
            >
              <X size={12} />
            </button>
          </div>
        </div>
      ) : null}

      <ConfirmDialog
        open={confirmState !== null}
        title={confirmMeta.title}
        description={confirmMeta.description}
        confirmLabel={confirmMeta.confirmLabel}
        danger={confirmMeta.danger}
        loading={confirmLoading}
        onConfirm={handleConfirmExecute}
        onCancel={cancelConfirm}
      />
    </PermissionGate>
  );
};
